import { useEffect, useMemo, useRef } from "react";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { buildEditedTranscriptBlocksForChapters } from "../ai/chaptersTranscript";

const normalizeText = (value: unknown): string =>
    String(value ?? "").replace(/\s+/g, " ").trim();

const isUntitledComposition = (value: unknown): boolean =>
    normalizeText(value).toLowerCase() === "untitled";

const sanitizeGeneratedTitle = (value: unknown): string | null => {
    const cleaned = normalizeText(value).replace(/^[“”"'`]+|[“”"'`]+$/g, "");
    if (!cleaned) return null;
    if (isUntitledComposition(cleaned)) return null;
    return cleaned.slice(0, 80).trim() || null;
};

type UseAutoCompositionTitleArgs = {
    enabled: boolean;
    projectId?: string | null;
    compositionId?: string | null;
    compositionTitle?: string | null;
    compositionRecord?: any | null;
    isCompositionEmpty: boolean;
    projectTracks: any[];
    getTranscript: (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;
    postAiCompositionTitle?: (args: {
        projectId: string;
        compositionId: string;
        blocks: Array<{ id: string; startSec: number; text: string }>;
    }) => Promise<{ title: string } | undefined>;
    renameComposition: (args: { compositionId: string; newName: string }) => Promise<void> | void;
    renameProjectIfUntitled?: (args: { projectId: string; newName: string }) => Promise<void> | void;
    onTitleUpdated?: (nextTitle: string) => void;
};

export const useAutoCompositionTitle = ({
    enabled,
    projectId,
    compositionId,
    compositionTitle,
    compositionRecord,
    isCompositionEmpty,
    projectTracks,
    getTranscript,
    postAiCompositionTitle,
    renameComposition,
    renameProjectIfUntitled,
    onTitleUpdated,
}: UseAutoCompositionTitleArgs): void => {
    const attemptedRef = useRef<Set<string>>(new Set());
    const inFlightRef = useRef<Set<string>>(new Set());
    const latestCompositionIdRef = useRef<string>("");
    const latestTitleRef = useRef<string>("");
    const getTranscriptRef = useRef(getTranscript);
    const postAiCompositionTitleRef = useRef(postAiCompositionTitle);
    const renameCompositionRef = useRef(renameComposition);
    const renameProjectIfUntitledRef = useRef(renameProjectIfUntitled);
    const onTitleUpdatedRef = useRef(onTitleUpdated);

    const safeProjectId = String(projectId ?? "").trim();
    const safeCompositionId = String(compositionId ?? "").trim();
    const effectiveTitle = normalizeText(
        compositionTitle ?? compositionRecord?.title ?? compositionRecord?.name ?? "",
    );

    const projectTracksSignature = useMemo(
        () =>
            (Array.isArray(projectTracks) ? projectTracks : [])
                .map((track: any) =>
                    [
                        String(track?.trackId || track?.id || track?.assetId || ""),
                        String(track?.activeTranscriptId || ""),
                        String(track?.transcriptionState || ""),
                    ].join(":"),
                )
                .join("|"),
        [projectTracks],
    );

    useEffect(() => {
        latestCompositionIdRef.current = safeCompositionId;
        latestTitleRef.current = effectiveTitle;
    }, [safeCompositionId, effectiveTitle]);

    useEffect(() => {
        getTranscriptRef.current = getTranscript;
        postAiCompositionTitleRef.current = postAiCompositionTitle;
        renameCompositionRef.current = renameComposition;
        renameProjectIfUntitledRef.current = renameProjectIfUntitled;
        onTitleUpdatedRef.current = onTitleUpdated;
    }, [getTranscript, postAiCompositionTitle, renameComposition, renameProjectIfUntitled, onTitleUpdated]);

    useEffect(() => {
        if (!enabled) return;
        if (!safeProjectId || !safeCompositionId) return;
        if (!postAiCompositionTitleRef.current) return;
        if (!CompositionService.isInitialized()) return;

        if (isCompositionEmpty) return;
        if (!isUntitledComposition(effectiveTitle)) return;
        if (attemptedRef.current.has(safeCompositionId)) return;
        if (inFlightRef.current.has(safeCompositionId)) return;

        let cancelled = false;
        inFlightRef.current.add(safeCompositionId);

        void (async () => {
            try {
                const { blocks } = await buildEditedTranscriptBlocksForChapters({
                    getTranscript: getTranscriptRef.current,
                    projectId: safeProjectId,
                });
                if (!blocks.length) {
                    return;
                }

                attemptedRef.current.add(safeCompositionId);

                const response = await postAiCompositionTitleRef.current?.({
                    projectId: safeProjectId,
                    compositionId: safeCompositionId,
                    blocks: blocks.map((block) => ({
                        id: block.id,
                        startSec: block.startSec,
                        text: block.text,
                    })),
                });

                const nextTitle = sanitizeGeneratedTitle(response?.title);
                if (!nextTitle) return;
                if (cancelled) return;
                if (latestCompositionIdRef.current !== safeCompositionId) return;
                if (!isUntitledComposition(latestTitleRef.current)) return;

                await renameCompositionRef.current({
                    compositionId: safeCompositionId,
                    newName: nextTitle,
                });

                await renameProjectIfUntitledRef.current?.({
                    projectId: safeProjectId,
                    newName: nextTitle,
                });

                if (latestCompositionIdRef.current === safeCompositionId) {
                    latestTitleRef.current = nextTitle;
                    onTitleUpdatedRef.current?.(nextTitle);
                }
            } catch (error) {
                console.error("[useAutoCompositionTitle] Failed to auto-title composition", error);
            } finally {
                inFlightRef.current.delete(safeCompositionId);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        enabled,
        safeProjectId,
        safeCompositionId,
        effectiveTitle,
        isCompositionEmpty,
        projectTracksSignature,
    ]);
};
